import { useMemo } from 'react'
import { itemOptions, questOptions } from '../../catalog'
import type { CatalogIndex } from '../../catalog/types'
import { validateQuest, type Issue } from '../../domain/validate'
import { TAGS, type ItemRef, type Quest, type Tag } from '../../domain/types'
import { BalancePanel } from '../BalancePanel/BalancePanel'
import { AddButton, NumberField, Section, TextField } from '../common/fields'
import {
  BoostRequirementRow,
  EstateRow,
  ItemRefRow,
  PrereqRow,
  SpecialRewardRow,
} from '../rows/rows'

const NEW_REF: ItemRef = { item: '', amount: 1, quality: 'Basic' }

export function QuestEditor({
  quest,
  catalog,
  allQuests,
  dirty,
  saving,
  onChange,
  onSave,
  onDiscard,
}: {
  quest: Quest
  catalog: CatalogIndex
  allQuests: Quest[]
  dirty: boolean
  saving: boolean
  onChange: (next: Quest) => void
  onSave: () => void
  onDiscard: () => void
}) {
  const items = useMemo(() => itemOptions(catalog), [catalog])
  const questChoices = useMemo(
    () =>
      questOptions(
        allQuests
          .filter((other) => other.id !== quest.id)
          .map((other) => ({
            id: other.id,
            label: other.label,
            gatingLevel: other.computed.gatingLevel,
          })),
      ),
    [allQuests, quest.id],
  )
  const knownIds = useMemo(() => new Set(allQuests.map((other) => other.id)), [allQuests])
  const issues = useMemo(
    () => validateQuest(quest, catalog, knownIds),
    [quest, catalog, knownIds],
  )
  const blocking = issues.filter((issue) => issue.severity === 'error')

  const set = <K extends keyof Quest>(key: K, value: Quest[K]) => onChange({ ...quest, [key]: value })

  function list<K extends keyof Quest>(key: K) {
    const current = quest[key] as unknown as unknown[]
    return {
      update: (index: number, next: unknown) =>
        set(key, current.map((row, i) => (i === index ? next : row)) as Quest[K]),
      remove: (index: number) => set(key, current.filter((_, i) => i !== index) as Quest[K]),
      add: (row: unknown) => set(key, [...current, row] as Quest[K]),
    }
  }

  const requirements = list('requirements')
  const toShow = list('requirementsToShow')
  const rewards = list('rewards')
  const prereqs = list('prereqs')
  const specials = list('specialRewards')
  const boosts = list('boostRequirement')
  const estates = list('estateRequirement')

  return (
    <div className="flex min-h-0 flex-1">
      <div className="flex min-w-0 flex-1 flex-col overflow-y-auto">
        <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-edge bg-ink/95 px-5 py-3 backdrop-blur">
          <div className="min-w-0 flex-1">
            <div className="truncate font-mono text-sm text-dim">
              quests/{quest.id}.json
            </div>
            <div className="flex items-center gap-2">
              <span className="truncate text-lg font-semibold text-parchment">
                {quest.label || <span className="text-dim">Untitled</span>}
              </span>
              {quest.disabled && (
                <span className="shrink-0 rounded bg-warn-bg px-1.5 py-0.5 text-[10px] font-bold uppercase text-warn">
                  WIP
                </span>
              )}
            </div>
          </div>
          {dirty && (
            <button
              type="button"
              onClick={onDiscard}
              className="rounded border border-edge px-3 py-1.5 text-sm text-dim hover:bg-ink-high"
            >
              Discard
            </button>
          )}
          <button
            type="button"
            onClick={onSave}
            disabled={!dirty || saving || blocking.length > 0}
            title={blocking.length > 0 ? 'Fix the errors below first' : undefined}
            className="rounded bg-gold px-4 py-1.5 text-sm font-medium text-ink hover:bg-gold-lit disabled:cursor-not-allowed disabled:bg-edge disabled:text-dim"
          >
            {saving ? 'Saving…' : dirty ? 'Commit to main' : 'Saved'}
          </button>
        </header>

        <IssueList issues={issues} />

        <div className="flex flex-col gap-4 p-5">
          <Section title="Identity">
            <div className="grid grid-cols-2 gap-3">
              <TextField label="Label" value={quest.label} onChange={(v) => set('label', v)} />
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium uppercase tracking-wide text-dim">Tag</span>
                <select
                  className="rounded border border-edge bg-ink-lift px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-gold"
                  value={quest.tag}
                  onChange={(event) => set('tag', event.target.value as Tag)}
                >
                  {TAGS.map((tag) => (
                    <option key={tag} value={tag}>
                      {tag}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <label className="flex items-start gap-2 rounded border border-edge bg-ink-deep p-2">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={quest.disabled}
                onChange={(event) => set('disabled', event.target.checked)}
              />
              <span className="flex flex-col">
                <span className="text-sm font-medium text-parchment">
                  Work in progress — never offered to the player
                </span>
                <span className="text-xs text-dim">
                  Stays in the project and in this editor, but the game skips it. Use this
                  instead of gating a quest behind a placeholder prereq.
                </span>
              </span>
            </label>
            <TextField
              label="Description"
              multiline
              value={quest.description}
              placeholder="The quest-giver's ask. {townName} is substituted at runtime."
              onChange={(v) => set('description', v)}
            />
            <TextField
              label="Completion text"
              multiline
              value={quest.completionText}
              onChange={(v) => set('completionText', v)}
            />
          </Section>

          <Section
            title="Gating"
            hint="Every prereq must pass for the quest to appear. Empty means available from the start."
          >
            {quest.prereqs.map((prereq, index) => (
              <PrereqRow
                key={index}
                value={prereq}
                catalog={catalog}
                questChoices={questChoices}
                onChange={(next) => prereqs.update(index, next)}
                onRemove={() => prereqs.remove(index)}
              />
            ))}
            <AddButton onClick={() => prereqs.add({ type: 'QuestCompleted', quest: '' })}>
              Add prereq
            </AddButton>
          </Section>

          <Section title="Requirements — consumed" hint="Taken from the player on turn-in.">
            {quest.requirements.map((ref, index) => (
              <ItemRefRow
                key={index}
                value={ref}
                options={items}
                onChange={(next) => requirements.update(index, next)}
                onRemove={() => requirements.remove(index)}
              />
            ))}
            <AddButton onClick={() => requirements.add({ ...NEW_REF })}>Add requirement</AddButton>
          </Section>

          <Section
            title="Requirements to show — kept"
            hint="The player must own these but keeps them. Excluded from estimatedValue."
          >
            {quest.requirementsToShow.map((ref, index) => (
              <ItemRefRow
                key={index}
                value={ref}
                options={items}
                onChange={(next) => toShow.update(index, next)}
                onRemove={() => toShow.remove(index)}
              />
            ))}
            <AddButton onClick={() => toShow.add({ ...NEW_REF })}>Add item to show</AddButton>
          </Section>

          <Section title="Other costs">
            <div className="grid grid-cols-3 gap-3">
              <NumberField
                label="Gold required"
                value={quest.goldRequirement}
                onChange={(v) => set('goldRequirement', v)}
              />
              <NumberField
                label="Gold to show"
                value={quest.goldRequirementToShow}
                onChange={(v) => set('goldRequirementToShow', v)}
              />
              <NumberField
                label="Renown required"
                value={quest.renownRequirement}
                onChange={(v) => set('renownRequirement', v)}
              />
              <NumberField
                label="Challenge stars"
                value={quest.challengeStarsObtained}
                onChange={(v) => set('challengeStarsObtained', v)}
              />
            </div>

            <div className="mt-2 text-xs font-medium uppercase tracking-wide text-dim">
              Estates — any one satisfies it
            </div>
            {quest.estateRequirement.map((estateId, index) => (
              <EstateRow
                key={index}
                value={estateId}
                catalog={catalog}
                onChange={(next) => estates.update(index, next)}
                onRemove={() => estates.remove(index)}
              />
            ))}
            <AddButton onClick={() => estates.add('')}>Add estate</AddButton>

            <div className="mt-2 text-xs font-medium uppercase tracking-wide text-dim">
              Boosts used
            </div>
            {quest.boostRequirement.map((boost, index) => (
              <BoostRequirementRow
                key={index}
                value={boost}
                catalog={catalog}
                onChange={(next) => boosts.update(index, next)}
                onRemove={() => boosts.remove(index)}
              />
            ))}
            <AddButton onClick={() => boosts.add({ boost: '', amount: 1 })}>Add boost</AddButton>
          </Section>

          <Section title="Rewards">
            {quest.rewards.map((ref, index) => (
              <ItemRefRow
                key={index}
                value={ref}
                options={items}
                onChange={(next) => rewards.update(index, next)}
                onRemove={() => rewards.remove(index)}
              />
            ))}
            <AddButton onClick={() => rewards.add({ ...NEW_REF })}>Add reward</AddButton>
            <div className="mt-2 max-w-[12rem]">
              <NumberField
                label="Gold reward"
                value={quest.goldReward}
                onChange={(v) => set('goldReward', v)}
              />
            </div>
          </Section>

          <Section title="Special rewards" hint="Unlocks rather than inventory.">
            {quest.specialRewards.map((reward, index) => (
              <SpecialRewardRow
                key={index}
                value={reward}
                catalog={catalog}
                onChange={(next) => specials.update(index, next)}
                onRemove={() => specials.remove(index)}
              />
            ))}
            <AddButton onClick={() => specials.add({ type: 'Tool', tool: '' })}>
              Add special reward
            </AddButton>
          </Section>
        </div>
      </div>

      <BalancePanel quest={quest} catalog={catalog} />
    </div>
  )
}

function IssueList({ issues }: { issues: Issue[] }) {
  if (issues.length === 0) return null
  const errors = issues.filter((issue) => issue.severity === 'error')
  const warnings = issues.filter((issue) => issue.severity === 'warning')
  return (
    <div className="flex flex-col gap-1 border-b border-edge px-5 py-3">
      {errors.length > 0 && (
        <p className="text-xs font-semibold uppercase tracking-wide text-danger">
          {errors.length} error{errors.length === 1 ? '' : 's'} — Unity's import would abort
        </p>
      )}
      {[...errors, ...warnings].map((issue, index) => (
        <div
          key={index}
          className={`flex gap-2 rounded px-2 py-1 text-xs ${
            issue.severity === 'error' ? 'bg-danger-bg text-danger' : 'bg-warn-bg text-warn'
          }`}
        >
          <code className="shrink-0 font-mono opacity-70">{issue.field}</code>
          <span>{issue.message}</span>
        </div>
      ))}
    </div>
  )
}
